import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Eye, EyeSlash, EnvelopeSimple, LockSimple, User as UserIcon, ArrowRight, Heart, ShieldCheck, Sparkle
} from "@phosphor-icons/react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthedUser, refresh } = useAuth();

  // Mode: "login" or "register" — defaults from query param ?mode=register
  const initialMode = new URLSearchParams(location.search).get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialMode);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const next = new URLSearchParams(location.search).get("next") || "/log";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(endpoint, body);
      setAuthedUser(data);
      await refresh();
      toast.success(mode === "login" ? `Welcome back, ${data.name || data.email}` : "Account created — welcome to MedEasy");
      navigate(next, { replace: true });
    } catch (e2) {
      const msg = formatApiErrorDetail(e2?.response?.data?.detail) || e2?.message || "Something went wrong";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] bg-[#F7F6F2]">
      {/* LEFT — form */}
      <div className="flex flex-col px-6 sm:px-12 lg:px-20 py-8 sm:py-12">
        {/* Header — logo */}
        <div className="flex items-center justify-between">
          <button
            data-testid="login-brand-home"
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
            aria-label="MedEasy home"
          >
            <img src="/medeasy-logo.png" alt="MedEasy — Medical Expenses & KM Tracker · Canada" className="h-12 sm:h-14 w-auto object-contain" />
          </button>
          <button
            data-testid="login-tab-switch"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-sm text-[#5C6660] hover:text-[#2C4C3B]"
          >
            {mode === "login" ? "Need an account? " : "Have an account? "}
            <span className="text-[#2C4C3B] font-semibold">{mode === "login" ? "Sign up" : "Sign in"} →</span>
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <div className="text-xs uppercase tracking-[0.22em] text-[#5C6660] font-semibold mb-3">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </div>
            <h1 className="font-[Manrope] text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
              {mode === "login" ? <>Sign in to <span className="text-[#2C4C3B]">MedEasy</span>.</> : <>Start tracking <br /><span className="text-[#2C4C3B]">in 30 seconds</span>.</>}
            </h1>
            <p className="text-[#5C6660] mt-3 text-base">
              {mode === "login"
                ? "Pick up where you left off. Your logbook, receipts, and exports are waiting."
                : "Track every kilometre, parking stub, and prescription in one calm place. Cancel anytime."}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5" autoComplete="on">
              {mode === "register" && (
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-sm font-medium text-[#1E2420]">Your name <span className="text-[#5C6660] font-normal">(optional)</span></Label>
                  <div className="relative">
                    <UserIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660]" />
                    <Input
                      id="name"
                      data-testid="login-name-input"
                      type="text"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Jamie Tremblay"
                      className="pl-10 h-12 rounded-xl border-[#E3E1D9] focus-visible:ring-[#2C4C3B]"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium text-[#1E2420]">Email</Label>
                <div className="relative">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660]" />
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10 h-12 rounded-xl border-[#E3E1D9] focus-visible:ring-[#2C4C3B]"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-medium text-[#1E2420]">
                  Password
                  {mode === "register" && <span className="text-[#5C6660] font-normal"> · 8+ characters</span>}
                </Label>
                <div className="relative">
                  <LockSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660]" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPw ? "text" : "password"}
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    required
                    minLength={mode === "register" ? 8 : undefined}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10 h-12 rounded-xl border-[#E3E1D9] focus-visible:ring-[#2C4C3B]"
                  />
                  <button
                    type="button"
                    data-testid="login-password-toggle"
                    onClick={() => setShowPw(!showPw)}
                    aria-label={showPw ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6660] hover:text-[#1E2420]"
                  >
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {mode === "login" && (
                  <div className="flex justify-end pt-1">
                    <button
                      type="button"
                      data-testid="login-forgot-password"
                      onClick={() => navigate(`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`)}
                      className="text-xs text-[#5C6660] hover:text-[#2C4C3B] underline underline-offset-4"
                    >
                      Forgot your password?
                    </button>
                  </div>
                )}
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  role="alert"
                  className="rounded-xl bg-[#C17767]/10 border border-[#C17767]/25 text-[#8C4A3D] px-4 py-3 text-sm"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                data-testid="login-submit"
                className="w-full h-12 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white font-semibold"
              >
                {busy
                  ? (mode === "login" ? "Signing in…" : "Creating account…")
                  : <>{mode === "login" ? "Sign in" : "Create account"} <ArrowRight size={18} className="ml-2" weight="bold" /></>}
              </Button>
            </form>

            {/* Hope Air strip — under form */}
            <div className="mt-8 rounded-2xl bg-white border border-[#E3E1D9] p-4 flex items-center gap-3">
              <img src="/hopeair-logo.png" alt="Hope Air" className="h-8 w-auto object-contain shrink-0" />
              <div className="flex-1 text-xs sm:text-sm text-[#5C6660] leading-snug">
                <strong className="text-[#1E2420]">10% of every MedEasy payment</strong> supports Hope Air — a Canadian charity that arranges free flights for people who can&apos;t afford to travel to medical care.{" "}
                <button onClick={() => navigate("/hope-air")} className="text-[#2C4C3B] underline font-medium" data-testid="login-hope-air-link">
                  Read more
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-xs text-[#5C6660] mt-8 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} weight="fill" className="text-[#3FA85E]" /> Encrypted &amp; private</span>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1.5"><Sparkle size={12} weight="fill" className="text-[#2E7BC4]" /> AI receipt scanning</span>
          <span aria-hidden>·</span>
          <span>Built in Canada 🇨🇦</span>
        </div>
      </div>

      {/* RIGHT — mountain background */}
      <div className="relative hidden lg:block">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/37840040/pexels-photo-37840040.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1280')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E2420]/55 via-[#2C4C3B]/30 to-[#1E2420]/70" />

        {/* Floating quote card */}
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-white text-xs uppercase tracking-[0.18em] font-semibold w-fit">
            <Heart weight="fill" size={12} className="text-[#D4A373]" /> Track yours · Help theirs
          </div>

          <div className="text-white">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-semibold">MedEasy community</div>
            <blockquote className="font-[Manrope] text-3xl sm:text-4xl font-bold tracking-tight leading-tight mt-3 max-w-md">
              &ldquo;Snap a parking stub.<br />AI fills the form.<br />
              <span className="text-[#D4A373]">Tax-ready at year end.</span>&rdquo;
            </blockquote>
            <p className="text-white/80 text-sm mt-4 max-w-md leading-relaxed">
              The Canadian medical-expense tracker built for tax time. 10% of every payment supports Hope Air.
            </p>

            {/* Hope Air partner badge */}
            <div className="mt-8 inline-flex items-center gap-3 bg-white rounded-2xl px-5 py-3 shadow-lg">
              <img src="/hopeair-logo.png" alt="Hope Air" className="h-7 w-auto object-contain" />
              <div className="text-xs text-[#1E2420]">
                <div className="font-semibold">10% supports Hope Air</div>
                <div className="text-[#5C6660]">Free flights for Canadian patients in need</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
